import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, FileText, Send, CircleX, Banknote } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY_ITEM = { name: "", spec: "", quantity: 1, unit: "", unit_price: 0 };
const EMPTY = {
  customer_id: "", project_id: "", title: "", issue_date: "", due_date: "",
  items: [{ ...EMPTY_ITEM }], tax_rate: 10, notes: "", terms: "",
};

const STATUS_COLORS = { 下書き: "text-gray-400", 発行済: "text-accent", 一部入金: "text-amber-400", 入金済: "text-green-400", 取消: "text-gray-600" };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const yen = (v) => `¥${Number(v || 0).toLocaleString()}`;

function PaymentModal({ invoice, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ date: "", amount: "", method: "振込", note: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get(`/admin/invoices/${invoice.id}`).then((r) => { setDetail(r.data); setError(""); }).catch(() => setError("入金情報を取得できませんでした。"));
  };
  useEffect(load, [invoice.id]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/invoices/${invoice.id}/payments`, { ...form, amount: Number(form.amount) || 0 });
      toast.success("入金を記録しました");
      setForm({ date: "", amount: "", method: "振込", note: "" });
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`${p.date}の入金（${yen(p.amount)}）を削除しますか？`)) return;
    try {
      await api.delete(`/admin/invoices/${invoice.id}/payments/${p.id}`);
      toast.success("削除しました");
      load();
      onChanged();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const remaining = detail ? detail.total - detail.paid_amount : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
      <div data-testid="payment-modal" className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto border border-line bg-coal p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-mincho text-lg font-bold text-white">入金管理（{invoice.invoice_number}）</h2>
          <button data-testid="payment-modal-close" onClick={onClose} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {error && <p data-testid="payment-load-error" role="alert" className="text-sm text-red-300">{error}</p>}
        {detail && (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">請求額</p><p data-testid="payment-invoice-total" className="font-mono text-sm font-bold text-white">{yen(detail.total)}</p></div>
              <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">入金済</p><p data-testid="payment-paid-total" className="font-mono text-sm font-bold text-green-400">{yen(detail.paid_amount)}</p></div>
              <div className="border border-line bg-ink p-3"><p className="font-mono text-[9px] text-gray-500">残額</p><p data-testid="payment-remaining-total" className="font-mono text-sm font-bold text-accent">{yen(remaining)}</p></div>
            </div>

            <form onSubmit={add} className="flex flex-wrap items-end gap-2 border border-line bg-ink p-3">
              <label className="block">
                <span className="font-mono text-[9px] tracking-[0.2em] text-gray-500">入金日</span>
                <input data-testid="payment-form-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={`mt-1 ${inputCls}`} />
              </label>
              <label className="block w-28">
                <span className="font-mono text-[9px] tracking-[0.2em] text-gray-500">金額 *</span>
                <input data-testid="payment-form-amount" type="number" min="1" max={remaining} required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={`mt-1 ${inputCls}`} />
              </label>
              <label className="block">
                <span className="font-mono text-[9px] tracking-[0.2em] text-gray-500">方法</span>
                <select data-testid="payment-form-method" value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} className={`mt-1 ${inputCls}`}>
                  <option>振込</option><option>現金</option><option>その他</option>
                </select>
              </label>
              <button data-testid="payment-use-remaining" type="button" disabled={saving || remaining <= 0} onClick={() => setForm(f => ({ ...f, amount: remaining }))} className="border border-line px-3 py-2.5 text-xs text-gray-300 transition-colors hover:text-accent disabled:opacity-40">残額を入力</button>
              <button data-testid="payment-form-submit" type="submit" disabled={saving || !form.amount || remaining <= 0} className="bg-accent px-4 py-2.5 font-display text-[11px] font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">
                記録
              </button>
            </form>

            <div className="space-y-1.5">
              {(detail.payments || []).map((p) => (
                <div key={p.id} data-testid={`payment-row-${p.id}`} className="flex items-center gap-3 border border-line bg-ink px-3 py-2">
                  <span className="font-mono text-[11px] text-gray-500">{p.date}</span>
                  <span className="font-mono text-xs font-bold text-green-400">{yen(p.amount)}</span>
                  <span className="text-[10px] text-gray-500">{p.method}</span>
                  <span className="flex-1 truncate text-xs text-gray-400">{p.note}</span>
                  <button data-testid={`payment-delete-${p.id}`} onClick={() => remove(p)} className="p-1 text-gray-500 transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {(detail.payments || []).length === 0 && <p className="text-xs text-gray-600">入金の記録はまだありません。</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminInvoices({ project, onChanged }) {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/invoices", { params: { ...(filterStatus ? { status: filterStatus } : {}), ...(project ? { project_id: project.id } : {}) } }).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
  };
  const changed = () => { load(); onChanged?.(); };
  const openNew = () => {
    setForm({ ...EMPTY, ...(project ? { customer_id: project.customer_id, project_id: project.id, title: project.name } : {}), items: [{ ...EMPTY_ITEM }] });
    setEditing({});
  };

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, project?.id]);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, it) => sum + Math.round((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)), 0);
    const tax = Math.floor((subtotal * (Number(form.tax_rate) || 0)) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_rate]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setItem = (i, key) => (e) =>
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [key]: e.target.value } : it)) }));

  const openEdit = (inv) => {
    setForm({
      ...EMPTY, ...inv,
      items: (inv.items || []).map((it) => ({ name: it.name, spec: it.spec || "", quantity: it.quantity, unit: it.unit || "", unit_price: it.unit_price })),
    });
    setEditing(inv);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      items: form.items.filter((it) => it.name.trim()).map((it) => ({ ...it, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 })),
      tax_rate: Number(form.tax_rate) || 0,
    };
    if (payload.items.length === 0) {
      toast.error("明細を1行以上入力してください");
      setSaving(false);
      return;
    }
    try {
      if (editing.id) await api.put(`/admin/invoices/${editing.id}`, payload);
      else await api.post("/admin/invoices", payload);
      toast.success("保存しました");
      setEditing(null);
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const issue = async (inv) => {
    if (!window.confirm(`請求書 ${inv.invoice_number} を発行しますか？\n発行後は内容を変更できません。`)) return;
    try {
      await api.post(`/admin/invoices/${inv.id}/issue`);
      toast.success("発行しました");
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const cancel = async (inv) => {
    if (!window.confirm(`請求書 ${inv.invoice_number} を取消にしますか？`)) return;
    try {
      await api.put(`/admin/invoices/${inv.id}/status`, { status: "取消" });
      toast.success("取消にしました");
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`請求書 ${inv.invoice_number} を削除しますか？`)) return;
    try {
      await api.delete(`/admin/invoices/${inv.id}`);
      toast.success("削除しました");
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openPdf = (inv) => window.open(`${api.defaults.baseURL}/admin/invoices/${inv.id}/pdf`, "_blank");
  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="admin-invoices-page" className="business-documents doc-invoices">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-mincho text-lg font-bold text-white">請求書・入金</h2>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">INVOICES — 見積書から作成、または直接作成</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="invoices-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての状態</option>
            <option>下書き</option><option>発行済</option><option>一部入金</option><option>入金済</option><option>取消</option>
          </select>
          <button data-testid="invoice-add-button" onClick={openNew} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
            <Plus className="h-4 w-4" />
            請求書を作成
          </button>
        </div>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[950px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">請求番号</th>
              <th className="px-4 py-3">顧客</th>
              <th className="px-4 py-3">件名</th>
              <th className="px-4 py-3">請求額</th>
              <th className="px-4 py-3">入金状況</th>
              <th className="px-4 py-3">支払期限</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-xs text-gray-300">{customerName(inv.customer_id)}</td>
                <td className="px-4 py-3 text-xs font-bold text-white">{inv.title}{inv.estimate_id && <span className="ml-1 text-[9px] text-gray-500">(見積より)</span>}</td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-white">{yen(inv.total)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-14 overflow-hidden rounded-full bg-line">
                      <div className="h-full bg-green-500" style={{ width: `${inv.total ? Math.min(100, (inv.paid_amount / inv.total) * 100) : 0}%` }} />
                    </div>
                    <span className="whitespace-nowrap font-mono text-[10px] text-gray-400">{yen(inv.paid_amount)}</span>
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-400">{inv.due_date || "—"}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-xs font-bold ${STATUS_COLORS[inv.status]}`}>{inv.status}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {inv.status === "下書き" && (
                      <>
                        <button data-testid={`invoice-edit-${inv.id}`} onClick={() => openEdit(inv)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="編集">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`invoice-issue-${inv.id}`} onClick={() => issue(inv)} className="border border-accent/50 p-2 text-accent transition-colors hover:bg-accent/10" title="発行">
                          <Send className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400" title="削除">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => openPdf(inv)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="PDF">
                      <FileText className="h-3.5 w-3.5" />
                    </button>
                    {(inv.status === "発行済" || inv.status === "一部入金" || inv.status === "入金済") && (
                      <button data-testid={`invoice-payments-${inv.id}`} onClick={() => setPaying(inv)} className="border border-green-500/40 p-2 text-green-400 transition-colors hover:bg-green-500/10" title="入金管理">
                        <Banknote className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {inv.status === "発行済" && (
                      <button data-testid={`invoice-cancel-${inv.id}`} onClick={() => cancel(inv)} className="border border-red-500/40 p-2 text-red-400 transition-colors hover:bg-red-500/10" title="取消">
                        <CircleX className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">請求書がありません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="invoice-form" onSubmit={save} className="max-h-[90vh] w-full max-w-3xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? `請求書を編集（${editing.invoice_number}）` : "請求書を作成"}</h2>
              <button type="button" data-testid="invoice-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="顧客 *">
                <select data-testid="invoice-form-customer" disabled={!!project} required value={form.customer_id} onChange={set("customer_id")} className={inputCls}>
                  <option value="">選択してください</option>
                  {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </Field>
              <Field label="関連する工事案件（任意）">
                <select data-testid="invoice-form-project" disabled={!!project} value={form.project_id} onChange={set("project_id")} className={inputCls}>
                  <option value="">—</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </Field>
              <Field label="件名 *"><input data-testid="invoice-form-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="造成工事一式" /></Field>
              <Field label="発行日（空欄で今日）"><input data-testid="invoice-form-issue-date" type="date" value={form.issue_date} onChange={set("issue_date")} className={inputCls} /></Field>
              <Field label="お支払期限"><input data-testid="invoice-form-due-date" type="date" value={form.due_date} onChange={set("due_date")} className={inputCls} /></Field>
              <Field label="消費税率">
                <select data-testid="invoice-form-tax-rate" value={form.tax_rate} onChange={set("tax_rate")} className={inputCls}>
                  <option value={10}>10%</option><option value={8}>8%</option><option value={0}>非課税</option>
                </select>
              </Field>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">明細</span>
                <button type="button" data-testid="invoice-item-add" onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }))} className="flex items-center gap-1 border border-line px-3 py-1.5 text-[11px] text-gray-300 transition-colors hover:border-accent hover:text-accent">
                  <Plus className="h-3.5 w-3.5" /> 行を追加
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} data-testid={`invoice-item-${i}`} className="grid grid-cols-12 items-center gap-2 border border-line bg-ink p-2">
                    <input data-testid={`invoice-item-name-${i}`} value={it.name} onChange={setItem(i, "name")} placeholder="品名 *" className={`col-span-4 ${inputCls}`} />
                    <input data-testid={`invoice-item-spec-${i}`} value={it.spec} onChange={setItem(i, "spec")} placeholder="仕様" className={`col-span-3 ${inputCls}`} />
                    <input data-testid={`invoice-item-qty-${i}`} type="number" step="any" value={it.quantity} onChange={setItem(i, "quantity")} placeholder="数量" className={`col-span-1 ${inputCls}`} />
                    <input data-testid={`invoice-item-unit-${i}`} value={it.unit} onChange={setItem(i, "unit")} placeholder="単位" className={`col-span-1 ${inputCls}`} />
                    <input data-testid={`invoice-item-price-${i}`} type="number" value={it.unit_price} onChange={setItem(i, "unit_price")} placeholder="単価" className={`col-span-2 ${inputCls}`} />
                    <div className="col-span-1 flex items-center justify-between gap-1">
                      <span className="font-mono text-[10px] text-gray-400">{yen(Math.round((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)))}</span>
                      <button type="button" data-testid={`invoice-item-remove-${i}`} onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} disabled={form.items.length === 1} className="text-gray-600 transition-colors hover:text-red-400 disabled:opacity-30">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div data-testid="invoice-form-totals" className="mt-3 space-y-1 border-t border-line pt-3 text-right text-sm">
                <p className="text-gray-400">小計: <span className="font-mono text-white">{yen(totals.subtotal)}</span></p>
                <p className="text-gray-400">消費税（{form.tax_rate}%）: <span className="font-mono text-white">{yen(totals.tax)}</span></p>
                <p className="text-base font-bold text-white">合計（税込）: <span className="font-mono text-accent">{yen(totals.total)}</span></p>
              </div>
            </div>

            <Field label="支払条件"><textarea data-testid="invoice-form-terms" rows={2} value={form.terms} onChange={set("terms")} className={`resize-y ${inputCls}`} placeholder="例：お支払期限までに下記口座へお振込みください" /></Field>
            <Field label="備考"><textarea data-testid="invoice-form-notes" rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>

            <button data-testid="invoice-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する（下書き）"}
            </button>
          </form>
        </div>
      )}

      {paying && <PaymentModal invoice={paying} onClose={() => setPaying(null)} onChanged={changed} />}
    </div>
  );
}
